import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  CheckCircle,
  AlertCircle,
  Camera,
  Save,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  X,
  User,
  Mail,
  Send,
  Download,
  FileSpreadsheet,
  Printer,
  MapPin,
  AlertTriangle,
  Clock,
  Filter,
} from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { ApiError, apiClient } from '../lib/apiClient';
import { ConfirmModal } from './ConfirmModal';
import { useNotify } from './useNotify';
import { logAudit, AUDIT_ACTIONS, AUDIT_ENTITIES } from '../lib/auditLog';
import {
  findScheduleViolations,
  resolvePropertyRule,
  parseStreetNumber,
  type PropertyWaterPolicy,
  type Violation,
  type ProgramLike,
  type DayAbbr,
} from '../lib/waterRestrictions';
import type {
  Controller,
  ZoneIssue,
  DoubleCheckInspection,
  ControllerInspectionData,
  ReportStyleSettings,
  AccountManagerData,
} from './report/types';
import { DEFAULT_REPORT_STYLE } from './report/types';
import { WaterRestrictionPolicyCard } from './WaterRestrictionPolicyCard';
import { InspectionTrailMap } from './InspectionTrailMap';

interface ReportTreeResponse {
  report: Record<string, unknown> & {
    id: string;
    job_site_name: string;
    job_site_id: string | null;
    month: string;
    technicians: string[] | null;
    is_draft: boolean | null;
    manager_notes: string | null;
    rejection_reason: string | null;
    email_sent_at: string | null;
    email_sent_to: string | null;
    created_at: string;
    status: { status_type: string; label: string; color: string } | null;
  };
  jobSite: Record<string, unknown> | null;
  controllers: Array<{
    id: string;
    controller_number: number;
    description: string;
    location: string;
    photo_url: string | null;
    water_start_times: unknown;
  }>;
  zoneInspections: Array<{
    id: string;
    controller_id: string | null;
    zone_number: number;
    type: string | null;
    work_codes: string;
    notes: string;
    photo_urls: string[];
  }>;
  zoneIssues: Array<{
    id: string;
    zone_inspection_id: string;
    work_code: string;
    notes: string;
    photo_urls: string[];
  }>;
  controllerInspections: Array<{
    id: string;
    controller_id: string | null;
    failure_codes: string[];
    notes: string;
    photo_urls: string[];
  }>;
  doublecheckInspections: Array<{
    id: string;
    doublecheck_description: string;
    doublecheck_size: string | null;
    failure_codes: string[];
    notes: string;
    photo_urls: string[];
  }>;
}

interface ManagerReportReviewProps {
  reportId: string;
  onBack: () => void;
  onApproved?: () => void;
}

// Local controller-inspection shape augments the shared one with the row id
// so inline note edits can target the correct PUT /api/controller-inspections/:id.
interface ControllerInspectionRow extends ControllerInspectionData {
  id: string;
}

/**
 * "Issues only" switch for the report output (FR f7fbed66). It governs the PDF
 * the manager downloads (and therefore prints) and the PDF that gets emailed —
 * both toolbars show the same switch, driven by one piece of state, so the
 * choice is never ambiguous. Photos are never filtered out.
 */
function IssuesOnlyToggle({
  active,
  onToggle,
  label,
  title,
  compact,
}: {
  active: boolean;
  onToggle: () => void;
  label: string;
  title: string;
  compact: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      title={title}
      className={`flex items-center gap-2 ${compact ? 'px-3 py-2' : 'px-4 py-3'} rounded-lg border font-semibold text-sm transition-colors ${
        active
          ? 'bg-amber-100 dark:bg-amber-500/20 border-amber-300 dark:border-amber-500/30 text-amber-800 dark:text-amber-300'
          : 'bg-surface border-line text-theme-secondary hover:bg-subtle'
      }`}
    >
      <Filter size={18} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export function ManagerReportReview({ reportId, onBack, onApproved }: ManagerReportReviewProps) {
  const { t, i18n } = useTranslation('inspection');
  const { t: tCommon } = useTranslation('common');
  const { notify, NotifyModal } = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<ReportTreeResponse['report'] | null>(null);
  const [jobSite, setJobSite] = useState<Record<string, unknown> | null>(null);
  const [waterPolicy, setWaterPolicy] = useState<PropertyWaterPolicy | null>(null);
  const [duration, setDuration] = useState<{
    enabled: boolean;
    activeMinutes: number;
    visitCount: number;
    eventCount: number;
  } | null>(null);
  const [reportTechs, setReportTechs] = useState<
    Array<{ name: string; email: string | null; roles: Array<'assigned' | 'performed' | 'submitted'> }> | null
  >(null);
  const [controllers, setControllers] = useState<Controller[]>([]);
  const [controllerInspections, setControllerInspections] = useState<ControllerInspectionRow[]>([]);
  const [doubleCheckInspections, setDoubleCheckInspections] = useState<DoubleCheckInspection[]>([]);
  const [workCodes, setWorkCodes] = useState<Array<{ code: string; is_issue: boolean }>>([]);
  const [doubleCheckWorkCodes, setDoubleCheckWorkCodes] = useState<Array<{ code: string; is_issue: boolean }>>([]);
  const [styleSettings, setStyleSettings] = useState<ReportStyleSettings>({ ...DEFAULT_REPORT_STYLE });

  const [managerNotes, setManagerNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['controllers', 'doublechecks', 'notes']));

  const [editingZoneIssue, setEditingZoneIssue] = useState<string | null>(null);
  const [editingZoneIssueNotes, setEditingZoneIssueNotes] = useState('');
  const [editingControllerNotes, setEditingControllerNotes] = useState<string | null>(null);
  const [editingControllerNotesValue, setEditingControllerNotesValue] = useState('');
  const [editingDCNotes, setEditingDCNotes] = useState<string | null>(null);
  const [editingDCNotesValue, setEditingDCNotesValue] = useState('');

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [successWasApproval, setSuccessWasApproval] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);
  const [accountManager, setAccountManager] = useState<AccountManagerData | null>(null);
  const [emailSending, setEmailSending] = useState(false);
  const [sendEmailOnApprove, setSendEmailOnApprove] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // Issues-only edition of the PDF (FR f7fbed66) — applies to the download and
  // to the emailed copy. Off by default: the full report stays the norm.
  const [issuesOnlyPdf, setIssuesOnlyPdf] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  // Ad-hoc "email this report to a chosen recipient" (FR 4bf151d9).
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<Array<{ name: string; email: string }>>([]);
  const [emailTo, setEmailTo] = useState(''); // the address to send to (typed or picked)
  const [emailToName, setEmailToName] = useState(''); // display name when picked from the list
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);
  // Non-blocking send: while set, a small "sending to X…" pill shows and the
  // modal is already closed so the manager isn't stuck watching a spinner
  // during the ~10s Azure send.
  const [sendingToRecipient, setSendingToRecipient] = useState<string | null>(null);

  useEffect(() => {
    fetchReportData();
  }, [reportId]);

  const fetchReportData = async () => {
    try {
      const [tree, workCodesData, workCodeTypesData, themesData] = await Promise.all([
        apiClient.get<ReportTreeResponse>(`/api/reports/${reportId}/tree`),
        apiClient.get<Array<{ id: string; code: string; is_issue: boolean | null }>>('/api/work-codes'),
        apiClient.get<Array<{ work_code_id: string; type_name: string }>>('/api/work-code-types'),
        apiClient.get<Array<{ is_active: boolean; report_colors: Partial<ReportStyleSettings> | null }>>('/api/themes'),
      ]);

      setReport(tree.report);
      setJobSite(tree.jobSite);
      setManagerNotes((tree.report.manager_notes as string | null) ?? '');

      // Load the property's water-restriction policy so we can flag controllers
      // whose programmed schedule violates it (manager-review only). Best-effort.
      if (tree.report.job_site_id) {
        apiClient
          .get<PropertyWaterPolicy>(`/api/job-sites/${tree.report.job_site_id}/water-restriction`)
          .then((p) => setWaterPolicy(p))
          .catch((err) => console.error('Failed to load water restriction policy:', err));
      }

      // Time-on-site (manager-only; gated/tuned by admin settings). Best-effort.
      apiClient
        .get<{ enabled: boolean; activeMinutes: number; visitCount: number; eventCount: number }>(
          `/api/reports/${reportId}/duration`,
        )
        .then((d) => setDuration(d))
        .catch((err) => console.error('Failed to load inspection duration:', err));

      // All technicians tied to the report (assigned/performed/submitted),
      // manager-review only. Best-effort.
      apiClient
        .get<{ technicians: Array<{ name: string; email: string | null; roles: Array<'assigned' | 'performed' | 'submitted'> }> }>(
          `/api/reports/${reportId}/technicians`,
        )
        .then((r) => setReportTechs(r.technicians ?? []))
        .catch((err) => console.error('Failed to load report technicians:', err));

      const idsByType = (typeName: string) =>
        new Set(workCodeTypesData.filter((tp) => tp.type_name === typeName).map((tp) => tp.work_code_id));
      const zoneTypeIds = idsByType('Zone');
      const doubleCheckTypeIds = idsByType('DoubleCheck');
      setWorkCodes(
        workCodesData
          .filter((wc) => zoneTypeIds.has(wc.id))
          .map((wc) => ({ code: wc.code, is_issue: wc.is_issue ?? false })),
      );
      setDoubleCheckWorkCodes(
        workCodesData
          .filter((wc) => doubleCheckTypeIds.has(wc.id))
          .map((wc) => ({ code: wc.code, is_issue: wc.is_issue ?? false })),
      );

      const activeTheme = themesData.find((th) => th.is_active);
      if (activeTheme?.report_colors) {
        setStyleSettings({ ...DEFAULT_REPORT_STYLE, ...activeTheme.report_colors });
      }

      // Account manager — non-critical
      const aspirePropertyId = (tree.jobSite as { aspire_property_id?: string | null } | null)?.aspire_property_id;
      if (aspirePropertyId) {
        try {
          const amData = await apiClient.get<{ account_manager: AccountManagerData }>(
            `/api/integrations/aspire/account-manager?propertyId=${aspirePropertyId}`,
          );
          setAccountManager(amData.account_manager ?? null);
        } catch {
          // Non-critical; leave null
        }
      }

      setControllerInspections(
        tree.controllerInspections.map((ci) => ({
          id: ci.id,
          controller_id: ci.controller_id ?? '',
          failure_codes: ci.failure_codes ?? [],
          notes: ci.notes ?? '',
          photo_urls: ci.photo_urls ?? [],
        })),
      );

      // Build the controller tree from tree.controllers + tree.zoneInspections + tree.zoneIssues.
      const issuesByZi = new Map<string, ZoneIssue[]>();
      for (const issue of tree.zoneIssues) {
        if (!issuesByZi.has(issue.zone_inspection_id)) {
          issuesByZi.set(issue.zone_inspection_id, []);
        }
        issuesByZi.get(issue.zone_inspection_id)!.push({
          id: issue.id,
          work_code: issue.work_code,
          notes: issue.notes ?? '',
          photo_urls: issue.photo_urls ?? [],
        });
      }

      const ctrlMap = new Map<string, Controller>();
      for (const c of tree.controllers) {
        ctrlMap.set(c.id, {
          id: c.id,
          controller_number: c.controller_number,
          description: c.description,
          location: c.location,
          photo_url: c.photo_url,
          water_start_times: c.water_start_times as Controller['water_start_times'],
          zone_inspections: [],
        });
      }
      for (const zi of tree.zoneInspections) {
        if (!zi.controller_id || !ctrlMap.has(zi.controller_id)) continue;
        ctrlMap.get(zi.controller_id)!.zone_inspections.push({
          id: zi.id,
          zone_number: zi.zone_number,
          type: zi.type ?? '',
          work_codes: zi.work_codes,
          notes: zi.notes ?? null,
          photo_urls: zi.photo_urls ?? [],
          issues: issuesByZi.get(zi.id) ?? [],
        });
      }
      const sorted = Array.from(ctrlMap.values())
        .filter((c) => c.zone_inspections.length > 0)
        .sort((a, b) => a.controller_number - b.controller_number);
      for (const c of sorted) c.zone_inspections.sort((a, b) => a.zone_number - b.zone_number);
      setControllers(sorted);

      setDoubleCheckInspections(
        tree.doublecheckInspections.map((dc) => ({
          id: dc.id,
          description: dc.doublecheck_description,
          size: parseFloat(dc.doublecheck_size ?? '0'),
          failure_codes: dc.failure_codes ?? [],
          notes: dc.notes ?? '',
          photo_urls: dc.photo_urls ?? [],
        })),
      );
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveManagerNotes = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/api/reports/${reportId}`, { manager_notes: managerNotes });
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { manager_notes: managerNotes },
        pageContext: 'review-report',
      });
      setHasUnsavedChanges(false);
      setNotesSaved(true);
    } catch (error) {
      console.error('Error saving manager notes:', error);
      // Surface the failure — a silently-swallowed save looked like "the note
      // won't let me save" (feedback 6ab32f3d). A 401 (session lapsed) is handled
      // globally: the app shows a re-auth prompt and restores this screen after
      // sign-in, so don't stack a second modal here.
      if (!(error instanceof ApiError && error.status === 401)) {
        notify(tCommon('status.errorTitle'), t('review.errorSaveNotes'), 'danger');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveZoneIssueNotes = async (issueId: string) => {
    try {
      await apiClient.put(`/api/zone-issues/${issueId}`, { notes: editingZoneIssueNotes });
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { zone_issue_id: issueId, notes: editingZoneIssueNotes },
        pageContext: 'review-report',
      });

      setControllers((prev) =>
        prev.map((c) => ({
          ...c,
          zone_inspections: c.zone_inspections.map((zi) => ({
            ...zi,
            issues: zi.issues?.map((issue) =>
              issue.id === issueId ? { ...issue, notes: editingZoneIssueNotes } : issue,
            ),
          })),
        })),
      );
      setEditingZoneIssue(null);
    } catch (error) {
      console.error('Error saving zone issue notes:', error);
    }
  };

  const handleSaveControllerNotes = async (inspectionId: string) => {
    try {
      await apiClient.put(`/api/controller-inspections/${inspectionId}`, { notes: editingControllerNotesValue });
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { controller_inspection_id: inspectionId, notes: editingControllerNotesValue },
        pageContext: 'review-report',
      });

      setControllerInspections((prev) =>
        prev.map((ci) => (ci.id === inspectionId ? { ...ci, notes: editingControllerNotesValue } : ci)),
      );
      setEditingControllerNotes(null);
    } catch (error) {
      console.error('Error saving controller notes:', error);
    }
  };

  const handleSaveDCNotes = async (inspectionId: string) => {
    try {
      await apiClient.put(`/api/doublecheck-inspections/${inspectionId}`, { notes: editingDCNotesValue });
      logAudit({
        action: AUDIT_ACTIONS.UPDATE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { doublecheck_inspection_id: inspectionId, notes: editingDCNotesValue },
        pageContext: 'review-report',
      });

      setDoubleCheckInspections((prev) =>
        prev.map((dc) => (dc.id === inspectionId ? { ...dc, notes: editingDCNotesValue } : dc)),
      );
      setEditingDCNotes(null);
    } catch (error) {
      console.error('Error saving double check notes:', error);
    }
  };

  const handleApprove = async () => {
    setSaving(true);
    try {
      // Save manager_notes first (works on submitted reports thanks to relaxed rule).
      if (hasUnsavedChanges) {
        await apiClient.put(`/api/reports/${reportId}`, { manager_notes: managerNotes });
        setHasUnsavedChanges(false);
      }
      await apiClient.post(`/api/reports/${reportId}/approve`);
      setShowApproveModal(false);

      logAudit({
        action: AUDIT_ACTIONS.APPROVE,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { manager_notes: managerNotes || null },
        pageContext: 'review-report',
      });

      let emailMsg = '';
      if (sendEmailOnApprove && accountManager?.email) {
        setEmailSending(true);
        try {
          const result = await apiClient.post<{ success: boolean; recipient?: string; error?: string }>(
            `/api/reports/${reportId}/email`,
          );
          if (result.success) {
            emailMsg = ' ' + t('review.emailSent', { recipient: result.recipient ?? accountManager.email });
          } else {
            emailMsg = ' ' + t('review.emailFailed', { error: result.error ?? t('review.unknownError') });
          }
        } catch (emailError) {
          const message = emailError instanceof ApiError ? emailError.message : t('review.unknownError');
          emailMsg = ' ' + t('review.emailDeliveryFailed', { msg: message });
        } finally {
          setEmailSending(false);
        }
      }

      setSuccessMessage(t('review.approvedMessage', { emailMsg }));
      setSuccessWasApproval(true);
      setShowSuccessModal(true);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : (error instanceof Error ? error.message : t('review.unknownError'));
      console.error('Error approving report:', error);
      setSuccessMessage(t('review.approveError', { msg: message }));
      setSuccessWasApproval(false);
      setShowSuccessModal(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) return;

    setSaving(true);
    try {
      // Save manager_notes before reject so the technician sees them on reopen.
      if (hasUnsavedChanges) {
        await apiClient.put(`/api/reports/${reportId}`, { manager_notes: managerNotes });
        setHasUnsavedChanges(false);
      }
      await apiClient.post(`/api/reports/${reportId}/reject`, { reason: rejectionReason });

      logAudit({
        action: AUDIT_ACTIONS.REJECT,
        entityType: AUDIT_ENTITIES.INSPECTION_REPORT,
        entityId: reportId,
        entityLabel: report ? `${report.job_site_name} - ${report.month}` : reportId,
        details: { rejection_reason: rejectionReason, manager_notes: managerNotes || null },
        pageContext: 'review-report',
      });

      setShowRejectModal(false);
      setSuccessMessage(t('review.rejectedMessage'));
      setSuccessWasApproval(false);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error rejecting report:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const { blob, filename } = await apiClient.getBlob(
        `/api/reports/${reportId}/pdf${issuesOnlyPdf ? '?issuesOnly=true' : ''}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'inspection_report.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      notify(tCommon('status.errorTitle'), t('review.errorPdf'), 'danger');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrintReport = () => {
    const originalTitle = document.title;
    document.title = ' ';
    window.print();
    document.title = originalTitle;
  };

  const handleDownloadXlsx = async () => {
    setDownloadingXlsx(true);
    try {
      const { blob, filename } = await apiClient.getBlob(`/api/reports/${reportId}/xlsx`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename ?? 'inspection_report.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading XLSX:', error);
      notify(tCommon('status.errorTitle'), t('review.errorXlsx'), 'danger');
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const openEmailModal = async () => {
    setEmailTo('');
    setEmailToName('');
    setEmailPickerOpen(false);
    setShowEmailModal(true);
    // Lazy-load the Site-user list the first time the modal is opened.
    if (emailRecipients.length === 0) {
      try {
        const list = await apiClient.get<Array<{ name: string; email: string }>>(
          '/api/reports/email-recipients',
        );
        setEmailRecipients(list);
      } catch (error) {
        console.error('Error loading email recipients:', error);
        // Non-fatal: the manager can still type an address by hand.
      }
    }
  };

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  // Poll the background send for its outcome. The server accepted the send (202)
  // and renders + transmits the PDF asynchronously (tens of seconds for a
  // photo-heavy report), recording the result to integration_logs; this reads it
  // back so we can confirm success/failure without holding an HTTP request open.
  const pollAdhocStatus = (to: string, since: string, attempt: number) => {
    const MAX_ATTEMPTS = 30; // ~90s at 3s spacing
    const again = () => {
      if (attempt + 1 >= MAX_ATTEMPTS) {
        setSendingToRecipient(null);
        notify(tCommon('status.successTitle'), t('review.emailStillSending'), 'warning');
      } else {
        window.setTimeout(() => pollAdhocStatus(to, since, attempt + 1), 3000);
      }
    };
    apiClient
      .get<{ pending: boolean; status?: string; message?: string; recipient?: string }>(
        `/api/reports/${reportId}/email-status?since=${encodeURIComponent(since)}`,
      )
      .then((s) => {
        if (s.pending) {
          again();
          return;
        }
        setSendingToRecipient(null);
        if (s.status === 'success') {
          notify(
            tCommon('status.successTitle'),
            t('review.emailAdhocSent', { recipient: s.recipient ?? to }),
            'success',
          );
        } else {
          notify(
            tCommon('status.errorTitle'),
            t('review.emailAdhocFailed', { error: s.message ?? t('review.unknownError') }),
            'danger',
          );
        }
      })
      .catch(() => again());
  };

  const handleSendAdhocEmail = () => {
    const to = emailTo.trim();
    const toName = emailToName;
    if (!isValidEmail(to)) {
      notify(tCommon('status.errorTitle'), t('review.emailInvalidAddress'), 'danger');
      return;
    }
    // Don't block on the send: the server accepts it immediately (202) and does
    // the slow render+transmit in the background. Close the modal, show the
    // non-blocking "sending…" pill, and poll for the result.
    setShowEmailModal(false);
    setSendingToRecipient(to);
    const since = new Date(Date.now() - 30_000).toISOString();
    apiClient
      .post(`/api/reports/${reportId}/email`, {
        to,
        toName: toName || undefined,
        issuesOnly: issuesOnlyPdf || undefined,
      })
      .then(() => pollAdhocStatus(to, since, 0))
      .catch((error) => {
        // The queue request itself was rejected (e.g. a 400) — report immediately.
        const message = error instanceof ApiError ? error.message : t('review.unknownError');
        setSendingToRecipient(null);
        notify(tCommon('status.errorTitle'), t('review.emailAdhocFailed', { error: message }), 'danger');
      });
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const formatMonth = (monthString: string) => {
    const [year, month] = monthString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString(i18n.language, {
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-theme-secondary">{t('review.loading')}</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="bg-surface rounded-xl shadow-sm border border-line p-12 text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-semibold text-theme-secondary mb-2">{t('review.notFound')}</h3>
          <button onClick={onBack} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold">
            {t('review.backToReports')}
          </button>
        </div>
      </div>
    );
  }

  const isSubmitted = report.status?.status_type === 'submitted';
  // The job_sites row id, for the water-restriction policy card and the GPS path
  // map. Prefer the live jobSite row id; fall back to the report's snapshot fk.
  const reviewJobSiteId =
    ((jobSite as { id?: string } | null)?.id) ?? (report.job_site_id ?? undefined);

  // Water-restriction violation detection (manager-review only). Resolve the
  // property's effective rule once, then flag any controller whose programmed
  // schedule breaks it. Reuses the same engine + i18n keys as the controller form.
  const waterRule = resolvePropertyRule(waterPolicy);
  const waterStreetNumber = parseStreetNumber(waterPolicy?.address);
  const dayLabel = (d: DayAbbr) => t(`schedule.day${d}`);
  const formatViolation = (v: Violation): string => {
    const city = waterPolicy?.city ?? tCommon('waterRestrictions.theCity', 'the city');
    if (v.kind === 'day') {
      return tCommon('waterRestrictions.violationDay', {
        program: v.program,
        days: (v.days ?? []).map(dayLabel).join(', '),
        allowed: (v.allowed ?? []).map(dayLabel).join(', '),
        city,
        defaultValue: `Program ${v.program} waters on ${(v.days ?? []).map(dayLabel).join(', ')}, which ${city} does not allow (allowed: ${(v.allowed ?? []).map(dayLabel).join(', ')}).`,
      });
    }
    if (v.kind === 'time') {
      return tCommon('waterRestrictions.violationTime', {
        program: v.program,
        window: v.window,
        defaultValue: `Program ${v.program} runs during the no-watering window (${v.window}).`,
      });
    }
    return tCommon('waterRestrictions.violationMaxDays', {
      count: v.count,
      max: v.max,
      city,
      defaultValue: `Schedule waters ${v.count} days/week; ${city} allows at most ${v.max}.`,
    });
  };
  const violationsForController = (controller: Controller): Violation[] => {
    if (!waterRule) return [];
    const programs = (controller.water_start_times as { programs?: ProgramLike[] } | null)?.programs;
    return findScheduleViolations(programs, waterRule, waterStreetNumber);
  };

  // Format active minutes as a localized "1h 45m" / "45m".
  const formatDuration = (mins: number): string => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    if (h > 0 && m > 0) return t('review.durationHm', { h, m, defaultValue: `${h}h ${m}m` });
    if (h > 0) return t('review.durationH', { h, defaultValue: `${h}h` });
    return t('review.durationM', { m, defaultValue: `${m}m` });
  };

  // Recipient combobox: filter the Site-user list by what's typed so far. An
  // empty query shows everyone; the manager can also just type any address.
  const emailQuery = emailTo.trim().toLowerCase();
  const filteredEmailRecipients = emailRecipients.filter((r) =>
    emailQuery === ''
      ? true
      : r.name.toLowerCase().includes(emailQuery) || r.email.toLowerCase().includes(emailQuery),
  );

  const totalControllers = controllers.length;
  const totalZones = controllers.reduce((sum, c) => sum + c.zone_inspections.length, 0);
  const zonesWithIssues = controllers.reduce(
    (sum, c) =>
      sum +
      c.zone_inspections.filter((z) => {
        if (z.issues && z.issues.length > 0) {
          return z.issues.some((issue) => {
            const wc = workCodes.find((w) => w.code === issue.work_code);
            return wc?.is_issue;
          });
        }
        return false;
      }).length,
    0,
  );

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-theme-secondary hover:text-theme-primary transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="font-medium">{t('review.backToReports')}</span>
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadXlsx}
            disabled={downloadingXlsx}
            className="p-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            title={t('viewer.exportExcel')}
          >
            {downloadingXlsx ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
          </button>
          <button
            onClick={handlePrintReport}
            className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title={t('viewer.printReport')}
          >
            <Printer size={18} />
          </button>
          <IssuesOnlyToggle
            active={issuesOnlyPdf}
            onToggle={() => setIssuesOnlyPdf((v) => !v)}
            compact
            label={t('review.issuesOnlyPdf', 'Issues only')}
            title={t(
              'review.issuesOnlyPdfHint',
              'When on, the PDF you download or email lists only the zones and equipment with issues. All photos are still included.',
            )}
          />
          <button
            onClick={openEmailModal}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
            title={t('review.emailReportTitle')}
          >
            <Send size={18} />
            <span className="hidden sm:inline">{t('review.emailReportButton')}</span>
          </button>
          {isSubmitted && (
            <>
              <button
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downloadingPdf ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                {downloadingPdf ? t('review.generating') : t('review.previewPdf')}
              </button>
              <button
                onClick={() => setShowRejectModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
              >
                <ThumbsDown size={18} />
                {t('review.reject')}
              </button>
              <button
                onClick={() => setShowApproveModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
              >
                <ThumbsUp size={18} />
                {t('review.approve')}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="rounded-xl shadow-sm border border-line overflow-hidden mb-6" style={{ backgroundColor: styleSettings.body_background_color }}>
        <div className="text-white px-6 py-4 flex items-center justify-between" style={{ backgroundColor: styleSettings.header_color }}>
          <div className="flex items-center gap-3">
            <CheckCircle size={28} />
            <div>
              <h2 className="text-xl font-bold">{t('review.title')}</h2>
              <p className="text-sm text-blue-100">{report.job_site_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-blue-100">{formatDate(report.created_at)}</span>
            {report.status && (
              <span
                className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded text-white"
                style={{ backgroundColor: report.status.color }}
              >
                {report.status.label}
              </span>
            )}
          </div>
        </div>

        <div className="p-6">
          {/* theme-allow: report summary sheet sits on inline body_background_color (always light); gray text recolored to fixed slate so it stays readable in every theme */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-lg p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-600 font-medium">{t('review.tileProperty')}</p>
                <p className="text-base text-slate-800 font-semibold">{report.job_site_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">{t('review.tileMonth')}</p>
                <p className="text-base text-slate-800">{formatMonth(report.month)}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 font-medium">{t('review.tileTechnicians')}</p>
                {reportTechs && reportTechs.length > 0 ? (
                  <div className="text-base text-slate-800 space-y-0.5">
                    {reportTechs.map((tech, i) => (
                      <div key={i} className="leading-snug">
                        {tech.name}
                        <span className="text-xs text-slate-500">
                          {' · '}
                          {tech.roles.map((r) => t(`review.techRole_${r}`)).join(', ')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-base text-slate-800">{report.technicians?.join(', ') || t('review.naFallback')}</p>
                )}
              </div>
              {(jobSite as { address?: string } | null)?.address && (
                <div>
                  <p className="text-sm text-slate-600 font-medium">{t('review.tileAddress')}</p>
                  <p className="text-base text-slate-800">{(jobSite as { address: string }).address}</p>
                </div>
              )}
              {duration?.enabled && duration.eventCount > 0 && (
                <div>
                  <p className="text-sm text-slate-600 font-medium flex items-center gap-1">
                    <Clock size={13} className="text-blue-400" />
                    {t('review.timeOnSiteLabel')}
                  </p>
                  <p className="text-base text-slate-800">
                    {formatDuration(duration.activeMinutes)}
                    <span className="text-slate-500"> · {t('review.visitsCount', { count: duration.visitCount })}</span>
                  </p>
                </div>
              )}
            </div>
            {accountManager && accountManager.name && (
              <div className="mt-4 pt-4 border-t border-blue-100 flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <User size={14} className="text-blue-400" />
                  <span className="text-sm text-slate-600 font-medium">{t('review.accountManagerLabel')}</span>
                  <span className="text-sm text-slate-800 font-semibold">{accountManager.name}</span>
                </div>
                {accountManager.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail size={13} className="text-blue-400" />
                    <span className="text-sm text-slate-700">{accountManager.email}</span>
                  </div>
                )}
                {accountManager.phone && (
                  <span className="text-sm text-slate-500">{accountManager.phone}</span>
                )}
              </div>
            )}
            {report.email_sent_at && report.email_sent_to && (
              <div className="mt-3 flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <Mail size={14} />
                <span>
                  <Trans
                    i18nKey="review.emailedOn"
                    ns="inspection"
                    values={{
                      addr: report.email_sent_to,
                      date: new Date(report.email_sent_at).toLocaleDateString(i18n.language, {
                        month: 'short', day: 'numeric', year: 'numeric',
                      }),
                    }}
                    components={{ strong: <strong /> }}
                  />
                </span>
              </div>
            )}
            {reviewJobSiteId && (
              <WaterRestrictionPolicyCard jobSiteId={reviewJobSiteId} className="mt-4" />
            )}
          </div>

          {/* Stat tiles sit on the always-light report summary sheet (inline body_background_color) — white fill kept, labels recolored to fixed slate */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border-2 border-blue-200 rounded-lg p-3">{/* theme-allow: report sheet stays light */}
              <p className="text-xs text-slate-600 font-medium">{t('review.tileControllers')}</p>
              <p className="text-xl font-bold text-blue-600">{totalControllers}</p>
            </div>
            <div className="bg-white border-2 border-green-200 rounded-lg p-3">{/* theme-allow: report sheet stays light */}
              <p className="text-xs text-slate-600 font-medium">{t('review.tileTotalZones')}</p>
              <p className="text-xl font-bold text-green-600">{totalZones}</p>
            </div>
            <div className="bg-white border-2 border-amber-200 rounded-lg p-3">{/* theme-allow: report sheet stays light */}
              <p className="text-xs text-slate-600 font-medium">{t('review.tileZoneIssues')}</p>
              <p className="text-xl font-bold text-amber-600">{zonesWithIssues}</p>
            </div>
            <div className="bg-white border-2 border-cyan-200 rounded-lg p-3">{/* theme-allow: report sheet stays light */}
              <p className="text-xs text-slate-600 font-medium">{t('review.tileDoubleChecks')}</p>
              <p className="text-xl font-bold text-cyan-600">{doubleCheckInspections.length}</p>
            </div>
          </div>
        </div>
      </div>

      {report.rejection_reason && report.status?.status_type !== 'rejected' && (
        <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4 mb-6">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-300">{t('review.previousRejection')}</p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">{report.rejection_reason}</p>
        </div>
      )}

      <div className="bg-surface rounded-xl shadow-sm border border-line mb-6">
        <button
          onClick={() => toggleSection('notes')}
          className="w-full flex items-center justify-between p-4 hover:bg-subtle transition-colors"
        >
          <div className="flex items-center gap-3">
            <MessageSquare size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-theme-primary">{t('review.managerNotes')}</h3>
            <span className="text-xs bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-full font-medium">{t('review.privateBadge')}</span>
          </div>
          {expandedSections.has('notes') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        {expandedSections.has('notes') && (
          <div className="px-4 pb-4 border-t border-line pt-4">
            <p className="text-xs text-theme-secondary mb-3">
              {t('review.notesVisibility')}
            </p>
            <textarea
              value={managerNotes}
              onChange={(e) => {
                setManagerNotes(e.target.value);
                setHasUnsavedChanges(true);
                setNotesSaved(false);
              }}
              rows={4}
              className="w-full bg-surface text-theme-primary border border-line rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
              placeholder={t('review.notesPlaceholder')}
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              {hasUnsavedChanges && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{t('review.unsavedChanges')}</span>
              )}
              {!hasUnsavedChanges && notesSaved && (
                <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 font-medium">
                  <CheckCircle size={14} />
                  {t('review.notesSaved')}
                </span>
              )}
              <button
                onClick={handleSaveManagerNotes}
                disabled={saving || !hasUnsavedChanges}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                {t('review.saveNotes')}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line mb-6">
        <button
          onClick={() => toggleSection('gpspath')}
          className="w-full flex items-center justify-between p-4 hover:bg-subtle transition-colors"
        >
          <div className="flex items-center gap-3">
            <MapPin size={20} className="text-blue-600" />
            <h3 className="text-lg font-semibold text-theme-primary">{t('review.gpsPathSection')}</h3>
          </div>
          {expandedSections.has('gpspath') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        {expandedSections.has('gpspath') && (
          <div className="px-4 pb-4 border-t border-line pt-4">
            <p className="text-xs text-theme-secondary mb-3">{t('review.gpsPathHint')}</p>
            <InspectionTrailMap reportId={reportId} />
          </div>
        )}
      </div>

      <div className="bg-surface rounded-xl shadow-sm border border-line mb-6">
        <button
          onClick={() => toggleSection('controllers')}
          className="w-full flex items-center justify-between p-4 hover:bg-subtle transition-colors"
        >
          <h3 className="text-lg font-semibold text-theme-primary">{t('review.controllerZoneSection')}</h3>
          {expandedSections.has('controllers') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>
        {expandedSections.has('controllers') && (
          <div className="px-4 pb-4 space-y-4">
            {controllers.map((controller) => {
              const ci = controllerInspections.find((i) => i.controller_id === controller.id);
              return (
                <div key={controller.id} className="border border-line rounded-lg overflow-hidden">
                  {/* Controller header sits on inline controller_header_color (always light); text kept fixed slate so it stays readable in every theme */}
                  <div className="px-4 py-3 border-b border-line" style={{ backgroundColor: styleSettings.controller_header_color }}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-slate-800 text-sm">
                          {t('report.controllerLabel', { n: controller.controller_number })}
                        </h4>
                        <p className="text-xs text-slate-600">
                          {controller.description} {controller.location && `- ${controller.location}`}
                        </p>
                      </div>
                      {ci && ci.failure_codes.length > 0 && (
                        <div className="text-xs">
                          <span className="font-medium text-slate-700">{t('review.codesLabel')} </span>
                          <span className="text-slate-600">{ci.failure_codes.join(', ')}</span>
                        </div>
                      )}
                    </div>
                    {ci && ci.notes && (
                      <div className="mt-2 flex items-start gap-2">
                        <p className="text-xs text-slate-600 italic flex-1">
                          <span className="font-medium not-italic">{t('review.techNotesLabel')} </span>
                          {editingControllerNotes === ci.id ? (
                            <span className="block mt-1">
                              <input
                                type="text"
                                value={editingControllerNotesValue}
                                onChange={(e) => setEditingControllerNotesValue(e.target.value)}
                                className="w-full bg-surface text-theme-primary border border-line rounded px-2 py-1 text-xs"
                              />
                              <span className="flex gap-1 mt-1">
                                <button onClick={() => handleSaveControllerNotes(ci.id)} className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded hover:bg-blue-700">{tCommon('actions.save')}</button>
                                <button onClick={() => setEditingControllerNotes(null)} className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5 rounded hover:bg-slate-300">{tCommon('actions.cancel')}</button>
                              </span>
                            </span>
                          ) : (
                            <>
                              {ci.notes}
                              {isSubmitted && (
                                <button
                                  onClick={() => {
                                    setEditingControllerNotes(ci.id);
                                    setEditingControllerNotesValue(ci.notes);
                                  }}
                                  className="ml-2 text-blue-500 hover:text-blue-700 inline-flex items-center"
                                >
                                  <Pencil size={12} />
                                </button>
                              )}
                            </>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {(() => {
                    const violations = violationsForController(controller);
                    if (violations.length === 0) return null;
                    return (
                      <div className="px-4 py-2 bg-red-50 dark:bg-red-500/10 border-b border-red-200 dark:border-red-500/30">
                        <div className="flex items-start gap-2 text-xs text-red-800 dark:text-red-300">
                          <AlertTriangle size={14} className="mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                          <div>
                            <span className="font-semibold">{t('review.waterViolationLabel')}</span>
                            <ul className="mt-0.5 space-y-0.5 list-disc list-inside">
                              {violations.map((v, i) => (
                                <li key={i}>{formatViolation(v)}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="border-b border-line" style={{ backgroundColor: styleSettings.table_header_color }}>
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold w-16" style={{ color: styleSettings.table_header_label_color }}>{t('report.colZone')}</th>
                          <th className="px-3 py-2 text-left font-semibold w-20" style={{ color: styleSettings.table_header_label_color }}>{t('report.colType')}</th>
                          <th className="px-3 py-2 text-left font-semibold w-20" style={{ color: styleSettings.table_header_label_color }}>{t('report.colStatus')}</th>
                          <th className="px-3 py-2 text-left font-semibold" style={{ color: styleSettings.table_header_label_color }}>{t('report.colWorkCodes')}</th>
                          <th className="px-3 py-2 text-left font-semibold" style={{ color: styleSettings.table_header_label_color }}>{t('report.colNotes')}</th>
                          <th className="px-3 py-2 text-left font-semibold w-16" style={{ color: styleSettings.table_header_label_color }}>{t('report.colPhotos')}</th>
                          {isSubmitted && <th className="px-3 py-2 text-left font-semibold w-16" style={{ color: styleSettings.table_header_label_color }}></th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line">
                        {controller.zone_inspections.map((zone) => {
                          const zoneWorkCodes =
                            zone.issues && zone.issues.length > 0
                              ? zone.issues.map((i) => i.work_code).join(', ')
                              : zone.work_codes;
                          const zoneNotes =
                            zone.issues && zone.issues.length > 0
                              ? zone.issues.map((i) => i.notes).filter(Boolean).join('; ')
                              : zone.notes || '';
                          const hasIssues =
                            zone.issues && zone.issues.length > 0
                              ? zone.issues.some((i) => {
                                  const wc = workCodes.find((w) => w.code === i.work_code);
                                  return wc?.is_issue;
                                })
                              : false;

                          const uniquePhotos = new Set<string>();
                          if (zone.issues && zone.issues.length > 0) {
                            zone.issues.forEach((i) => i.photo_urls.forEach((u) => uniquePhotos.add(u)));
                          } else if (zone.photo_urls) {
                            zone.photo_urls.forEach((u) => uniquePhotos.add(u));
                          }

                          return (
                            <tr key={zone.id} className="hover:bg-subtle">
                              <td className="px-3 py-2 font-medium text-blue-700">{zone.zone_number}</td>
                              <td className="px-3 py-2 text-theme-secondary uppercase">{zone.type}</td>
                              <td className="px-3 py-2">
                                {hasIssues ? (
                                  <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                                    <AlertCircle size={12} />
                                    <span>{t('report.statusIssue')}</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                                    <CheckCircle size={12} />
                                    <span>{t('report.statusOk')}</span>
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <span className={hasIssues ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'}>
                                  {zoneWorkCodes}
                                </span>
                              </td>
                              <td className="px-3 py-2 max-w-xs">
                                {zone.issues && zone.issues.length > 0 ? (
                                  <div className="space-y-1">
                                    {zone.issues.map((issue) => (
                                      <div key={issue.id} className="text-xs">
                                        {editingZoneIssue === issue.id ? (
                                          <div className="flex items-center gap-1">
                                            <input
                                              type="text"
                                              value={editingZoneIssueNotes}
                                              onChange={(e) => setEditingZoneIssueNotes(e.target.value)}
                                              className="flex-1 bg-surface text-theme-primary border border-line rounded px-2 py-0.5 text-xs"
                                            />
                                            <button onClick={() => handleSaveZoneIssueNotes(issue.id)} className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded hover:bg-blue-700">
                                              <CheckCircle size={12} />
                                            </button>
                                            <button onClick={() => setEditingZoneIssue(null)} className="text-xs bg-subtle text-theme-primary px-1.5 py-0.5 rounded hover:bg-subtle">
                                              <X size={12} />
                                            </button>
                                          </div>
                                        ) : (
                                          <span className="text-theme-secondary italic">
                                            {issue.notes || '—'}
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-theme-secondary italic">{zoneNotes || '—'}</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {uniquePhotos.size > 0 && (
                                  <span className="inline-flex items-center gap-1 text-teal-600">
                                    <Camera size={12} />
                                    <span>{uniquePhotos.size}</span>
                                  </span>
                                )}
                              </td>
                              {isSubmitted && (
                                <td className="px-3 py-2">
                                  {zone.issues && zone.issues.length > 0 && (
                                    <button
                                      onClick={() => {
                                        const firstIssue = zone.issues![0];
                                        setEditingZoneIssue(firstIssue.id);
                                        setEditingZoneIssueNotes(firstIssue.notes);
                                      }}
                                      className="text-blue-500 hover:text-blue-700"
                                      title={t('review.editNotes')}
                                    >
                                      <Pencil size={14} />
                                    </button>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {controller.zone_inspections.some(
                    (z) =>
                      (z.issues && z.issues.some((i) => i.photo_urls.length > 0)) ||
                      z.photo_urls.length > 0,
                  ) && (
                    <div className="px-3 py-3 bg-subtle border-t border-line">
                      <h5 className="text-sm font-semibold text-theme-primary mb-3">{t('review.zonePhotos')}</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {controller.zone_inspections.flatMap((zone) => {
                          const photos: string[] = [];
                          if (zone.issues && zone.issues.length > 0) {
                            zone.issues.forEach((i) => i.photo_urls.forEach((u) => photos.push(u)));
                          } else if (zone.photo_urls) {
                            photos.push(...zone.photo_urls);
                          }
                          return [...new Set(photos)].map((url, idx) => (
                            <div key={`${zone.id}-${idx}`} className="border border-line rounded-lg p-2 bg-surface">
                              <p className="text-xs font-medium text-theme-primary mb-1">{t('review.zoneLabel', { n: zone.zone_number })}</p>
                              <img
                                src={url}
                                alt={t('review.zoneAlt', { n: zone.zone_number })}
                                className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => window.open(url, '_blank')}
                              />
                            </div>
                          ));
                        })}
                      </div>
                    </div>
                  )}

                  {ci && ci.photo_urls && ci.photo_urls.length > 0 && (
                    <div className="px-3 py-3 bg-amber-50 dark:bg-amber-500/10 border-t border-amber-200 dark:border-amber-500/30">
                      <h5 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-3">{t('review.controllerErrorPhotos')}</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {ci.photo_urls.map((url, idx) => (
                          <div key={idx} className="border border-amber-200 dark:border-amber-500/30 rounded-lg p-2 bg-surface">
                            <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">{t('review.errorPhotoLabel', { n: idx + 1 })}</p>
                            <img
                              src={url}
                              alt={t('review.controllerErrorPhotoAlt', { n: controller.controller_number, idx: idx + 1 })}
                              className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                              onClick={() => window.open(url, '_blank')}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {doubleCheckInspections.length > 0 && (
        <div className="bg-surface rounded-xl shadow-sm border border-line mb-6">
          <button
            onClick={() => toggleSection('doublechecks')}
            className="w-full flex items-center justify-between p-4 hover:bg-subtle transition-colors"
          >
            <h3 className="text-lg font-semibold text-theme-primary">{t('report.dcSectionTitle')}</h3>
            {expandedSections.has('doublechecks') ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </button>
          {expandedSections.has('doublechecks') && (
            <div className="px-4 pb-4">
              <div className="border border-line rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="border-b border-line" style={{ backgroundColor: styleSettings.table_header_color }}>
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: styleSettings.table_header_label_color }}>{t('report.colDescription')}</th>
                      <th className="px-3 py-2 text-left font-semibold w-20" style={{ color: styleSettings.table_header_label_color }}>{t('report.colSize')}</th>
                      <th className="px-3 py-2 text-left font-semibold w-20" style={{ color: styleSettings.table_header_label_color }}>{t('report.colStatus')}</th>
                      <th className="px-3 py-2 text-left font-semibold" style={{ color: styleSettings.table_header_label_color }}>{t('report.colFailureCodesNotes')}</th>
                      <th className="px-3 py-2 text-left font-semibold w-16" style={{ color: styleSettings.table_header_label_color }}>{t('report.colPhotos')}</th>
                      {isSubmitted && <th className="px-3 py-2 text-left font-semibold w-16" style={{ color: styleSettings.table_header_label_color }}></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {doubleCheckInspections.map((dc) => {
                      const hasIssues =
                        dc.failure_codes.length > 0 &&
                        dc.failure_codes.some((code) => {
                          const wc = doubleCheckWorkCodes.find((w) => w.code === code);
                          return wc?.is_issue;
                        });
                      return (
                        <tr key={dc.id} className="hover:bg-subtle">
                          <td className="px-3 py-2 font-medium text-theme-primary">{dc.description}</td>
                          <td className="px-3 py-2 text-theme-secondary">{dc.size}"</td>
                          <td className="px-3 py-2">
                            {hasIssues ? (
                              <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                                <AlertCircle size={12} />
                                <span>{t('report.statusIssue')}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                                <CheckCircle size={12} />
                                <span>{t('report.statusOk')}</span>
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <div>
                              <span className={hasIssues ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}>
                                {dc.failure_codes.join(', ') || t('review.noIssues')}
                              </span>
                              {editingDCNotes === dc.id ? (
                                <div className="flex items-center gap-1 mt-1">
                                  <input
                                    type="text"
                                    value={editingDCNotesValue}
                                    onChange={(e) => setEditingDCNotesValue(e.target.value)}
                                    className="flex-1 bg-surface text-theme-primary border border-line rounded px-2 py-0.5 text-xs"
                                  />
                                  <button onClick={() => handleSaveDCNotes(dc.id)} className="text-xs bg-blue-600 text-white px-1.5 py-0.5 rounded hover:bg-blue-700">
                                    <CheckCircle size={12} />
                                  </button>
                                  <button onClick={() => setEditingDCNotes(null)} className="text-xs bg-subtle text-theme-primary px-1.5 py-0.5 rounded hover:bg-subtle">
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : (
                                dc.notes && (
                                  <span className="text-theme-secondary italic ml-1">({dc.notes})</span>
                                )
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {dc.photo_urls.length > 0 && (
                              <span className="inline-flex items-center gap-1 text-teal-600">
                                <Camera size={12} />
                                <span>{dc.photo_urls.length}</span>
                              </span>
                            )}
                          </td>
                          {isSubmitted && (
                            <td className="px-3 py-2">
                              <button
                                onClick={() => {
                                  setEditingDCNotes(dc.id);
                                  setEditingDCNotesValue(dc.notes);
                                }}
                                className="text-blue-500 hover:text-blue-700"
                                title={t('review.editNotes')}
                              >
                                <Pencil size={14} />
                              </button>
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {doubleCheckInspections.some((dc) => dc.photo_urls.length > 0) && (
                <div className="mt-4">
                  <h5 className="text-sm font-semibold text-theme-primary mb-3">{t('review.dcPhotos')}</h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {doubleCheckInspections.flatMap((dc) =>
                      dc.photo_urls.map((url, idx) => (
                        <div key={`${dc.id}-${idx}`} className="border border-line rounded-lg p-2 bg-surface">
                          <p className="text-xs font-medium text-theme-primary mb-1">{dc.description}</p>
                          <img
                            src={url}
                            alt={dc.description}
                            className="w-full h-32 object-cover rounded cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(url, '_blank')}
                          />
                        </div>
                      )),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isSubmitted && (
        <div className="flex items-center justify-end gap-2 mb-8">
          <button
            onClick={handleDownloadXlsx}
            disabled={downloadingXlsx}
            className="p-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            title={t('viewer.exportExcel')}
          >
            {downloadingXlsx ? <Loader2 size={18} className="animate-spin" /> : <FileSpreadsheet size={18} />}
          </button>
          <button
            onClick={handlePrintReport}
            className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title={t('viewer.printReport')}
          >
            <Printer size={18} />
          </button>
          <IssuesOnlyToggle
            active={issuesOnlyPdf}
            onToggle={() => setIssuesOnlyPdf((v) => !v)}
            compact={false}
            label={t('review.issuesOnlyPdf', 'Issues only')}
            title={t(
              'review.issuesOnlyPdfHint',
              'When on, the PDF you download or email lists only the zones and equipment with issues. All photos are still included.',
            )}
          />
          <button
            onClick={handleDownloadPdf}
            disabled={downloadingPdf}
            className="flex items-center gap-2 px-6 py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {downloadingPdf ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
            {downloadingPdf ? t('review.generating') : t('review.previewPdf')}
          </button>
          <button
            onClick={() => setShowRejectModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            <ThumbsDown size={18} />
            {t('review.rejectReport')}
          </button>
          <button
            onClick={() => setShowApproveModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold"
          >
            <ThumbsUp size={18} />
            {t('review.approveReport')}
          </button>
        </div>
      )}

      <ConfirmModal
        isOpen={showApproveModal}
        title={t('review.approveTitle', 'Approve Report')}
        message={t('review.approveMessage', 'Are you sure you want to approve this inspection report? Once approved, it will be available for download by account managers.')}
        confirmText={emailSending ? t('review.sendingEmail', 'Sending report email...') : t('review.approve', 'Approve')}
        cancelText={t('common:cancel', 'Cancel')}
        variant="success"
        onConfirm={handleApprove}
        onCancel={() => setShowApproveModal(false)}
        isLoading={saving || emailSending}
        loadingText={emailSending ? t('review.sendingEmail', 'Sending report email...') : t('review.approving', 'Approving...')}
      >
        {accountManager?.email && (
          <label className="flex items-center gap-3 p-3 bg-subtle rounded-lg cursor-pointer select-none hover:bg-subtle transition-colors">
            <input
              type="checkbox"
              checked={sendEmailOnApprove}
              onChange={(e) => setSendEmailOnApprove(e.target.checked)}
              className="w-4 h-4 rounded border-line text-green-600 focus:ring-green-500"
            />
            <div className="flex items-center gap-2 min-w-0">
              <Mail size={16} className="text-theme-secondary flex-shrink-0" />
              <span className="text-sm text-theme-primary">
                {t('review.emailAccountManager', 'Email report to account manager')}
                <span className="text-theme-secondary ml-1">({accountManager.email})</span>
              </span>
            </div>
          </label>
        )}
      </ConfirmModal>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-xl shadow-2xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center">
                  <ThumbsDown className="text-red-600 dark:text-red-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-theme-primary mb-2">{t('review.rejectModalTitle')}</h3>
                  <p className="text-theme-secondary text-sm mb-4">
                    {t('review.rejectModalMessage')}
                  </p>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={4}
                    className="w-full bg-surface text-theme-primary border border-line rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
                    placeholder={t('review.rejectPlaceholder')}
                    autoFocus
                  />
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-subtle rounded-b-xl flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                }}
                disabled={saving}
                className="px-4 py-2 text-theme-primary bg-surface border border-line rounded-lg hover:bg-subtle transition-colors font-medium"
              >
                {tCommon('actions.cancel')}
              </button>
              <button
                onClick={handleReject}
                disabled={saving || !rejectionReason.trim()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving && <Loader2 className="animate-spin" size={18} />}
                {saving ? t('review.rejecting') : t('review.rejectReport')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showSuccessModal}
        title={tCommon('status.successTitle')}
        message={successMessage}
        confirmText={tCommon('actions.ok')}
        variant="success"
        onConfirm={() => {
          setShowSuccessModal(false);
          if (successWasApproval) {
            onApproved?.();
          }
          onBack();
        }}
        onCancel={() => {
          setShowSuccessModal(false);
          onBack();
        }}
      />

      {showEmailModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowEmailModal(false)}
        >
          <div
            className="bg-surface rounded-xl shadow-xl border border-line w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <div className="flex items-center gap-2">
                <Send size={18} className="text-indigo-600" />
                <h3 className="text-lg font-semibold text-theme-primary">{t('review.emailModalTitle')}</h3>
              </div>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-theme-secondary hover:text-theme-primary transition-colors"
                aria-label={tCommon('actions.cancel')}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-theme-secondary">
                {t('review.emailModalDescription', { property: report.job_site_name })}
              </p>
              <div className="relative">
                <label className="block text-sm font-medium text-theme-primary mb-1">
                  {t('review.emailRecipientLabel')}
                </label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => {
                    setEmailTo(e.target.value);
                    setEmailToName('');
                    setEmailPickerOpen(true);
                  }}
                  onFocus={() => setEmailPickerOpen(true)}
                  placeholder={t('review.emailRecipientPlaceholder')}
                  className="w-full px-3 py-2 rounded-lg border border-line bg-surface text-theme-primary placeholder:text-theme-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  autoFocus
                />
                {emailPickerOpen && filteredEmailRecipients.length > 0 && (
                  <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded-lg border border-line bg-surface shadow-lg">
                    {filteredEmailRecipients.map((r) => (
                      <li key={r.email}>
                        <button
                          type="button"
                          onClick={() => {
                            setEmailTo(r.email);
                            setEmailToName(r.name);
                            setEmailPickerOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-subtle transition-colors"
                        >
                          <span className="block text-sm text-theme-primary">{r.name}</span>
                          <span className="block text-xs text-theme-secondary">{r.email}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={issuesOnlyPdf}
                  onChange={(e) => setIssuesOnlyPdf(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-indigo-600"
                />
                <span className="text-sm text-theme-primary">
                  {t('review.issuesOnlyEmailLabel', 'Send the issues-only version')}
                  <span className="block text-xs text-theme-secondary">
                    {t(
                      'review.issuesOnlyEmailHint',
                      'Lists only the zones and equipment with issues, and says on the report that it is filtered. All photos are still included.',
                    )}
                  </span>
                </span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-line">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 py-2 rounded-lg text-theme-secondary hover:bg-subtle transition-colors"
              >
                {tCommon('actions.cancel')}
              </button>
              <button
                onClick={handleSendAdhocEmail}
                disabled={!isValidEmail(emailTo)}
                className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send size={18} />
                {t('review.emailSendButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {sendingToRecipient && (
        <div className="fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-surface border border-line rounded-lg shadow-lg">
          <Loader2 size={16} className="animate-spin text-indigo-600" />
          <span className="text-sm text-theme-primary">
            {t('review.emailSendingToast', { recipient: sendingToRecipient })}
          </span>
        </div>
      )}

      {NotifyModal}
    </div>
  );
}
